import React from 'react';
import { useFormikContext } from 'formik';
import { 
  Box, 
  Typography, 
  Card, 
  CardMedia, 
  CardContent, 
  CardActions, 
  Button, 
  Chip,
  Stack,
  Divider,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { styled } from '@mui/material/styles';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PersonIcon from '@mui/icons-material/Person';
import LuggageIcon from '@mui/icons-material/Luggage';
import { motion } from 'framer-motion';

import { BookingFormValues } from '../../pages/BookingPage';
import GridItem from '../common/GridItem';
import GridContainer from '../common/GridContainer';
import ResponsiveTypography from '../common/ResponsiveTypography';

// Define vehicle type interface
interface Vehicle {
  id: string;
  name: string;
  image: string;
  description: string;
  passengers: number;
  luggage: number;
  features: string[];
  hourlyRate: number;
  distanceRate: number;
}

// Sample vehicle data
const vehicles: Vehicle[] = [
  {
    id: 'luxury-sedan',
    name: 'Luxury Sedan',
    image: '/images/vehicles/luxury-sedan.jpg',
    description: 'Premium sedan with exceptional comfort for business travel.',
    passengers: 3,
    luggage: 3,
    features: ['Free WiFi', 'Bottled Water', 'Phone Chargers', 'Climate Control'],
    hourlyRate: 6225,
    distanceRate: 291
  },
  {
    id: 'executive-suv',
    name: 'Executive SUV',
    image: '/images/vehicles/executive-suv.jpg',
    description: 'Spacious SUV with premium amenities for group travel.',
    passengers: 6,
    luggage: 6,
    features: ['Free WiFi', 'Bottled Water', 'Phone Chargers', 'Extra Legroom', 'Climate Control'],
    hourlyRate: 7885,
    distanceRate: 353
  },
  {
    id: 'luxury-van',
    name: 'Luxury Van',
    image: '/images/vehicles/luxury-van.jpg',
    description: 'Premium van offering spacious seating for larger groups.',
    passengers: 10,
    luggage: 10,
    features: ['Free WiFi', 'Bottled Water', 'Phone Chargers', 'Extra Legroom', 'Tablet Access'],
    hourlyRate: 10375,
    distanceRate: 457
  },
  {
    id: 'stretch-limousine',
    name: 'Stretch Limousine',
    image: '/images/vehicles/stretch-limousine.jpg',
    description: 'Ultimate luxury experience with premium amenities.',
    passengers: 8,
    luggage: 6,
    features: ['Champagne Service', 'Premium Sound System', 'Ambient Lighting', 'VIP Experience'],
    hourlyRate: 12450,
    distanceRate: 519
  }
];

// Styled components for responsive cards
const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'transform 0.3s, box-shadow 0.3s',
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: theme.shadows[8]
  },
  [theme.breakpoints.down('sm')]: {
    borderRadius: '12px',
    boxShadow: theme.shadows[4],
  }
}));

const VehicleSelectionForm: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  
  const { values, setFieldValue, errors, touched } = useFormikContext<BookingFormValues>();

  const handleVehicleSelect = (vehicleId: string) => {
    setFieldValue('vehicleId', vehicleId);
  };

  // Filter vehicles based on passenger and luggage count
  const suitableVehicles = vehicles.filter(vehicle => 
    vehicle.passengers >= values.passengers && 
    vehicle.luggage >= values.luggage
  );

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };
  
  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.4 }
    }
  };

  return (
    <>
      <ResponsiveTypography 
        variant="h5" 
        sx={{ 
          fontWeight: 600, 
          mb: { xs: 2, sm: 3 },
          textAlign: isMobile ? 'center' : 'left'
        }}
      >
        Select Your Vehicle
      </ResponsiveTypography>
      
      {suitableVehicles.length === 0 ? (
        <ResponsiveTypography 
          variant="body1" 
          color="text.secondary" 
          sx={{ 
            mt: 2, 
            mb: 4,
            textAlign: isMobile ? 'center' : 'left' 
          }}
        >
          No vehicles available for {values.passengers} passengers and {values.luggage} luggage items. 
          Please adjust your requirements.
        </ResponsiveTypography>
      ) : (
        <Box sx={{ mb: 4 }}>
          <ResponsiveTypography 
            variant="body1" 
            color="text.secondary" 
            sx={{ 
              mb: { xs: 2, sm: 4 },
              textAlign: isMobile ? 'center' : 'left'
            }}
          >
            Choose from our premium fleet - all vehicles include professional chauffeur service
          </ResponsiveTypography>
          
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            <GridContainer spacing={isMobile ? 2 : 3}>
              {suitableVehicles.map((vehicle) => (
                <GridItem key={vehicle.id} xs={12} sm={6} lg={4}>
                  <motion.div variants={cardVariants}>
                    <StyledCard 
                      sx={{ 
                        border: values.vehicleId === vehicle.id ? 2 : 0,
                        borderColor: 'primary.main',
                        position: 'relative',
                        minHeight: '500px',
                        display: 'flex',
                        flexDirection: 'column'
                      }}
                    >
                      {values.vehicleId === vehicle.id && (
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 10,
                            right: 10,
                            zIndex: 2,
                            bgcolor: 'primary.main',
                            color: 'white',
                            borderRadius: '50%',
                            width: 32,
                            height: 32,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: 2
                          }}
                        >
                          <CheckCircleIcon fontSize={isMobile ? "medium" : "small"} />
                        </Box>
                      )}
                      <CardMedia
                        component="img"
                        height="240"
                        image={vehicle.image}
                        alt={vehicle.name}
                        sx={{ 
                          objectFit: 'cover',
                          width: '100%',
                          aspectRatio: '16/9'
                        }}
                      />
                      <CardContent sx={{ 
                        flexGrow: 1, 
                        p: isMobile ? 2.5 : 2,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between'
                      }}>
                        <Box>
                          <ResponsiveTypography 
                            variant="h6" 
                            component="div" 
                            gutterBottom
                            sx={{ fontWeight: 600 }}
                          >
                            {vehicle.name}
                          </ResponsiveTypography>
                          
                          <ResponsiveTypography 
                            variant="body2" 
                            color="text.secondary" 
                            sx={{ mb: 2 }}
                          >
                            {vehicle.description}
                          </ResponsiveTypography>
                          
                          <Stack 
                            direction={isMobile ? "column" : "row"} 
                            spacing={isMobile ? 1 : 2} 
                            sx={{ mb: 2 }}
                          >
                            <Chip 
                              icon={<PersonIcon />} 
                              label={`${vehicle.passengers} Passengers`} 
                              size={isMobile ? "medium" : "small"} 
                              variant="outlined" 
                              sx={{ height: isMobile ? 36 : 'auto' }}
                            />
                            <Chip 
                              icon={<LuggageIcon />} 
                              label={`${vehicle.luggage} Luggage`} 
                              size={isMobile ? "medium" : "small"} 
                              variant="outlined" 
                              sx={{ height: isMobile ? 36 : 'auto' }}
                            />
                          </Stack>
                          
                          <ResponsiveTypography variant="subtitle2" gutterBottom>Features</ResponsiveTypography>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
                            {vehicle.features.map((feature, index) => (
                              <Chip 
                                key={index} 
                                label={feature} 
                                size={isMobile ? "medium" : "small"} 
                                sx={{ mb: 0.5, height: isMobile ? 32 : 'auto' }} 
                              />
                            ))}
                          </Box>
                          
                          <ResponsiveTypography 
                            variant="subtitle1" 
                            color="primary.main" 
                            sx={{ fontWeight: 600, mt: 1 }}
                          >
                            {values.tripType === 'hourly' 
                              ? `₹${vehicle.hourlyRate}/hour`
                              : `₹${vehicle.distanceRate}/km`}
                          </ResponsiveTypography>
                        </Box>
                      </CardContent>
                      
                      <Divider />
                      
                      <CardActions sx={{ p: isMobile ? 2 : 1 }}>
                        <Button 
                          fullWidth 
                          variant={values.vehicleId === vehicle.id ? "contained" : "outlined"}
                          color="primary"
                          onClick={() => handleVehicleSelect(vehicle.id)}
                          sx={{ 
                            py: isMobile ? 1.5 : 1,
                            fontSize: isMobile ? '1rem' : 'inherit'
                          }}
                        >
                          {values.vehicleId === vehicle.id ? "Selected" : "Select"}
                        </Button>
                      </CardActions>
                    </StyledCard>
                  </motion.div>
                </GridItem>
              ))}
            </GridContainer>
          </motion.div>
        </Box>
      )}
      
      {touched.vehicleId && errors.vehicleId && (
        <ResponsiveTypography 
          color="error" 
          variant="body2" 
          sx={{ 
            mt: 1,
            textAlign: isMobile ? 'center' : 'left' 
          }}
        >
          {errors.vehicleId}
        </ResponsiveTypography>
      )}
    </>
  );
};

export default VehicleSelectionForm; 